import type { ComponentPropsWithRef } from 'react';

import { cn } from '../lib/cn';
import type { IconSize } from '../tokens';
import { Icon } from './Icon';
import type { IconName } from './registry';

/**
 * - `light` — the brand sheet as drawn: a dark-green line on a light mint tile,
 *   a stronger mint when active.
 * - `dark` — the same tile translated to the rail: a light line on an ink tile,
 *   and for the active item a mint line on the brand-green tile.
 */
export type IconTileSurface = 'light' | 'dark';

const tileSize: Record<IconSize, string> = {
    sm: 'size-[var(--icon-tile-sm)] rounded-[var(--radius-control)]',
    md: 'size-[var(--icon-tile-md)] rounded-[var(--radius-control-lg)]',
    lg: 'size-[var(--icon-tile-lg)] rounded-[var(--radius-card)]',
};

const surfaceStyles: Record<IconTileSurface, { rest: string; active: string }> = {
    light: {
        rest: 'bg-[var(--icon-tile-bg)] text-[var(--icon-tile-fg)]',
        active: 'bg-[var(--icon-tile-bg-active)] text-[var(--icon-tile-fg)]',
    },
    dark: {
        rest: cn(
            'bg-[var(--rail-icon-tile)] text-[var(--rail-icon)]',
            'group-hover:bg-[var(--rail-icon-tile-hover)] group-hover:text-[var(--rail-icon-hover)]',
        ),
        active: 'bg-[var(--rail-icon-tile-active)] text-[var(--rail-icon-active)]',
    },
};

export type IconTileProps = Omit<ComponentPropsWithRef<'span'>, 'children'> & {
    name: IconName;
    /** Tile 26 · 32 · 40px with a 16 · 18 · 20px icon. */
    size?: IconSize;
    surface?: IconTileSurface;
    /** The current item: stronger mint on light, brand green on dark. */
    active?: boolean;
    /** Accessible name; without one the tile is decorative. */
    label?: string;
};

/**
 * An icon on a rounded tile, as on the client's brand sheet. Use it where the
 * icon *identifies* something — a navigation item, a panel, a card — and a bare
 * `Icon` where it only decorates an action inside a button.
 *
 * On `dark`, put `group` on the interactive parent: the tile brightens with
 * the item's hover.
 */
export function IconTile({
    name,
    size = 'md',
    surface = 'light',
    active = false,
    label,
    className,
    ...rest
}: IconTileProps) {
    const styles = surfaceStyles[surface];

    return (
        <span
            className={cn(
                'inline-flex shrink-0 items-center justify-center',
                tileSize[size],
                active ? styles.active : styles.rest,
                className,
            )}
            data-active={active || undefined}
            {...rest}
        >
            <Icon name={name} size={size} label={label} />
        </span>
    );
}
